import type { FieldReadFunction, TypePolicies } from '@apollo/client';
import { isUXPEnvironment } from '@frameio/utils/config';
import type { LinkedSequence } from '../../../generated/types';
import type { GetAllExportsXmpMetadataQuery } from './queries/generated/GetAllExportsXmpMetadata';
import { GetAllExportsXmpMetadata } from './queries/generated/GetAllExportsXmpMetadata';

let cachedExportsArray: unknown[] | null = null;
let cachedExportsMap: Map<string, LinkedSequence[]> | null = null;

/**
 * Builds a Map for O(1) lookups of export metadata by assetId or versionId.
 * Each key maps to an array of LinkedSequence so multiple sequence links per
 * asset are preserved.
 *
 * Memoized via reference equality - Apollo maintains referential stability, so
 * the map is only rebuilt when the underlying data actually changes.
 */
function getExportsMap(
  exports: GetAllExportsXmpMetadataQuery['getAllExportsXmpMetadata']
): Map<string, LinkedSequence[]> {
  if (exports === cachedExportsArray && cachedExportsMap) {
    return cachedExportsMap;
  }

  const exportsMap = new Map<string, LinkedSequence[]>();

  for (const item of exports) {
    if (item && item.__typename === 'SequenceExportsXMPMetadata') {
      const sequenceName = item.sequenceName ?? null;
      const sequenceId = item.sequenceId ?? null;
      const data: LinkedSequence = { sequenceName, sequenceId };

      if (item.assetId) {
        const arr = exportsMap.get(item.assetId) ?? [];
        arr.push(data);
        exportsMap.set(item.assetId, arr);
      }
      if (item.versionId) {
        const arr = exportsMap.get(item.versionId) ?? [];
        arr.push(data);
        exportsMap.set(item.versionId, arr);
      }
    }
  }

  cachedExportsArray = exports;
  cachedExportsMap = exportsMap;

  return exportsMap;
}

/**
 * Shared read function for the sequences client field. Returns all Premiere
 * sequences this asset is linked to.
 */
const linkedSequencesReadFn: FieldReadFunction<LinkedSequence[]> = (
  _,
  { cache, readField }
) => {
  if (!isUXPEnvironment()) {
    return [];
  }

  const assetId = readField('id') as string | undefined;

  if (!assetId) {
    return [];
  }

  try {
    const result = cache.readQuery({
      query: GetAllExportsXmpMetadata,
    });
    const exports = result?.getAllExportsXmpMetadata;

    if (!exports || !Array.isArray(exports)) {
      return [];
    }

    const index = getExportsMap(exports);
    const arr = index.get(assetId) ?? [];
    return arr.map(
      (e): LinkedSequence => ({
        __typename: 'LinkedSequence',
        sequenceId: e.sequenceId ?? null,
        sequenceName: e.sequenceName ?? null,
      })
    );
  } catch {
    return [];
  }
};

/**
 * Shared field policies for sequence linking. Applied to all asset types.
 */
const sequenceLinkedClientFields = {
  sequences: {
    read: linkedSequencesReadFn,
  },
};

/**
 * Type policies for Premiere Pro integration.
 */
export const typePolicies: TypePolicies = {
  VideoAsset: { fields: sequenceLinkedClientFields },
  AudioAsset: { fields: sequenceLinkedClientFields },
  VersionStackAsset: { fields: sequenceLinkedClientFields },
  Query: {
    fields: {
      getAllExportsXmpMetadata: {
        merge(existing, incoming) {
          return Array.isArray(incoming) ? incoming : existing ?? [];
        },
      },
    },
  },
  MarkerSyncStatus: {
    keyFields: (object) =>
      object.sequenceId ? ['assetId', 'sequenceId'] : ['assetId'],
  },
  PremiereSequence: {
    fields: {
      metadata: {
        merge(existing, incoming) {
          return Array.isArray(incoming) ? incoming : existing ?? [];
        },
      },
    },
  },
  SequenceExportsXMPMetadata: {
    fields: {
      sequenceName: {
        read(existing) {
          return existing ?? null;
        },
      },
    },
  },
  Subscription: {
    fields: {
      sequenceEvents: {
        merge: true,
      },
    },
  },
};
